import React, { useEffect, useRef, useState } from "react";
import styles from "../styles/OtpVerification.module.css";
import { sendEmailOtp, verifyEmailOtp } from "../utils/authUtils";

const EMPTY_CODE = ["", "", "", "", "", ""];

const OtpVerification = ({
  email,
  shouldCreateUser = false,
  onVerified,
  onBack,
  title = "Enter Your Code",
  subtitle = "We sent a 6-digit code to",
}) => {
  const [digits, setDigits] = useState(EMPTY_CODE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const submittingRef = useRef(false);
  const verifiedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const submit = async (code) => {
    if (submittingRef.current || verifiedRef.current) return;

    submittingRef.current = true;
    setLoading(true);
    setError("");
    try {
      const data = await verifyEmailOtp(email, code);
      verifiedRef.current = true;
      onVerified?.(data);
    } catch (err) {
      if (verifiedRef.current) return;
      setError(err.message || "Verification failed. Please try again.");
      setDigits(EMPTY_CODE);
      inputRefs.current[0]?.focus();
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleChange = (value, index) => {
    if (submittingRef.current || verifiedRef.current) return;

    const cleaned = value.replace(/[^0-9]/g, "");
    const current = digits[index];

    // Paste / autofill of the full code into an empty box
    if (cleaned.length > 1 && !current) {
      const next = [...digits];
      for (let i = 0; i < 6 - index && i < cleaned.length; i++) {
        next[index + i] = cleaned[i];
      }
      setDigits(next);
      inputRefs.current[Math.min(index + cleaned.length, 5)]?.focus();
      if (next.every((digit) => digit !== "")) submit(next.join(""));
      return;
    }

    const digit = cleaned.slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (next.every((item) => item !== "") && index === 5) {
      submit(next.join(""));
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend || loading || verifiedRef.current) return;
    setError("");
    try {
      await sendEmailOtp(email, { shouldCreateUser });
      setTimer(60);
      setCanResend(false);
      setDigits(EMPTY_CODE);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || "Failed to resend code. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>
          {subtitle}
          <br />
          <span className={styles.email}>{email}</span>
        </p>

        <div className={styles.codeRow}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              className={`${styles.codeInput} ${
                digit ? styles.codeInputFilled : ""
              }`}
              value={digit}
              onChange={(event) => handleChange(event.target.value, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              disabled={loading}
              autoFocus={index === 0}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {error && <span className={styles.error}>{error}</span>}
        {loading && <span className={styles.loading}>Verifying...</span>}

        <button
          type="button"
          className={styles.resendButton}
          onClick={handleResend}
          disabled={!canResend || loading}
        >
          {canResend ? "Resend Code" : `Resend in ${timer}s`}
        </button>

        {onBack && (
          <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            disabled={loading}
          >
            Use a different email
          </button>
        )}
      </div>
    </div>
  );
};

export default OtpVerification;
